import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Circles } from "react-loader-spinner";
import { Helmet } from "react-helmet";
import Home from "./Theme2Components/Home";
import Login from "./Theme2Components/Login";
import SignUp from "./Theme2Components/SignUp";
import LoginDashboard from "./Theme2Components/LoginDashboard";
import AboutContainer from "./Theme2Components/AboutContainer";
import BlogDetail from "./Theme2Components/Blogs/BlogDetail";
import AuthBlogDetail from "./Theme2Components/Blogs/AuthBlogDetail";
import DropCv from "./Theme2Components/ContactUS/DropCv";
import Feedback from "./Theme2Components/ContactUS/Feedback";
import HospitalClinic from "./Theme2Components/Doctor/HospitalClinic";
import PageNotFound from "./Theme2Components/PageNotFound";
import AppointmentContainer from "./Theme2Components/ContactUS/AppointmentContainer";
import ContactUsPageWrapper from "./Theme2Components/ContactUS/ContactUsPageWrapper";
import BlogsWrapper from "./Theme2Components/Blogs/BlogsWrapper";
import ServicesPageWrapper from "./Theme2Components/Services/Pages/ServicesPageWrapper";
import ServicesDetail from "./Theme2Components/Services/Pages/ServicesDetail";
import ReviewsPageWrapper from "./Theme2Components/Reviews/Pages/ReviewsPageWrapper";
import FacilitiesDetail from "./Theme2Components/FacilitiesPages/FacilitiesDetail";
import FacilitiesPageWrapper from "./Theme2Components/FacilitiesPages/FacilitiesPageWrapper";
import NewsUpdatesWrapper from "./Theme2Components/NewsUpdates/Pages/NewsUpdatesWrapper";
import NewsDetail from "./Theme2Components/NewsUpdates/Pages/NewsDetail";
import CareerWrapper from "./Theme2Components/Hospital/CareerWrapper";
import DoctorDetail from "./Theme2Components/Hospital/DoctorListing/DoctorDetail";
import DoctorListingWrapper from "./Theme2Components/Hospital/DoctorListing/DoctorListingWrapper";
import GalleryWrapper from "./Theme2Components/GalleryLayouts/GalleryWrapper";
import GalleryDetail from "./Theme2Components/GalleryLayouts/GalleryDetail";
import FontLoader from "./Theme2Components/FontLoader";

// ✅ Base CSS (direct import for first render)
import "./Theme2Components/themeassets/css/bootstrap-icons.css";
import "./Theme2Components/themeassets/css/simple-line-icons.min.css";
import "./Theme2Components/themeassets/css/themify-icons.css";
import "./Theme2Components/themeassets/css/animate.min.css";
import "./Theme2Components/themeassets/css/style.css";
import "./Theme2Components/themeassets/css/color-schemes/red.css";
import CareerDetails from "./Theme2Components/Hospital/CareerDetails";
import ForgetPassword from "./Theme2Components/ForgetPassword";

function Theme2Routes({ isData, setIsdata, patientLogin }) {
  const sitedata = useSelector((store) => store.global.loginData);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);


  // ✅ Loader hide once site data loaded
  useEffect(() => {
    if (sitedata) {
      setLoading(false);
    }
  }, [sitedata]);

  // ✅ Favicon update
  useEffect(() => {
    if (sitedata?.site_logo) {
      const faviconLink = document.querySelector("link[rel='icon']");
      if (faviconLink) {
        faviconLink.href = sitedata.site_logo;
      } else {
        const newFaviconLink = document.createElement("link");
        newFaviconLink.rel = "icon";
        newFaviconLink.href = sitedata.site_logo;
        document.head.appendChild(newFaviconLink);
      }
    }
  }, [sitedata]);

  // ✅ Dynamic theme colors apply as CSS variables
  const primaryColor = sitedata?.options?.theme_primary_color || "#ffc400";
  const secondaryColor = sitedata?.options?.theme_secondary_color || "#fff";
  const hoverColor = sitedata?.options?.theme_hover_color || "#000";

  useEffect(() => {
    document.documentElement.style.setProperty("--primary-color", primaryColor);
    document.documentElement.style.setProperty("--hover-color", hoverColor);
    document.documentElement.style.setProperty("--secondary-color", secondaryColor);
  }, [primaryColor, secondaryColor, hoverColor]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{
          height: "100vh",
          width: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <Circles height="80" width="80" color="#000" ariaLabel="loading" />
      </div>
    );
  }

  return (
    <>
      {/* ✅ Helmet ensures title, fonts, and variables load first time */}
      <Helmet>
        {/* <title>{sitedata?.site_title || "Welcome - ClinTa Web Builder"}</title> */}

        {sitedata?.fonts?.banner?.link && (
          <link
            href={sitedata.fonts.banner.link.replace(
              "@import url('",
              ""
            ).replace("');", "")}
            rel="stylesheet"
          />
        )}
        {sitedata?.fonts?.heading?.link && (
          <link
            href={sitedata.fonts.heading.link.replace(
              "@import url('",
              ""
            ).replace("');", "")}
            rel="stylesheet"
          />
        )}
        {sitedata?.fonts?.paragraph?.link && (
          <link
            href={sitedata.fonts.paragraph.link.replace(
              "@import url('",
              ""
            ).replace("');", "")}
            rel="stylesheet"
          />
        )}

        <style>{`
          :root {
            ${sitedata?.fonts?.banner?.name ? `--banner-font: ${sitedata.fonts.banner.name};` : ""}
            ${sitedata?.fonts?.heading?.name ? `--heading-font: ${sitedata.fonts.heading.name};` : ""}
            ${sitedata?.fonts?.paragraph?.name ? `--paragraph-font: ${sitedata.fonts.paragraph.name};` : ""}
          }
        `}</style>
      </Helmet>

      <FontLoader />

      <Routes>
        <Route path="/" element={<Home isData={isData} setIsdata={setIsdata} />} />
        <Route path="/doctors" element={<DoctorListingWrapper />} />
        <Route path="/doctor/:id/:slug" element={<DoctorDetail />} />
        <Route path="/about" element={<AboutContainer />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/contact" element={<ContactUsPageWrapper />} />
        <Route path="/careers" element={<CareerWrapper />} />
        <Route path="/career/detail/:id" element={<CareerDetails />} />
        <Route path="/blogs" element={<BlogsWrapper />} />
        <Route path="/blog/detail/:slug" element={<BlogDetail />} />
        <Route path="/author/detail/:slug" element={<AuthBlogDetail />} />
        <Route path="/news" element={<NewsUpdatesWrapper />} />
        <Route path="/news/detail/:slug" element={<NewsDetail />} />
        <Route path="/reviews" element={<ReviewsPageWrapper />} />
        <Route path="forget-password/:slug" element={<ForgetPassword />} />
        {/* <Route path="/specialities" element={<ServicesPageWrapper />} />
        <Route path="/speciality/:id/:slug" element={<ServicesDetail />} /> */}

        {(sitedata?.site_type === "hospitals" || !sitedata?.site_type) ? (
          <>
            <Route path="/specialities" element={<ServicesPageWrapper />} />
            <Route path="/speciality/:id/:slug" element={<ServicesDetail />} />
          </>
        ) : sitedata?.site_type === "doctors" ? (
          <>
            <Route path="/services" element={<ServicesPageWrapper />} />
            <Route path="/service/:id/:slug" element={<ServicesDetail />} />
          </>
        ) : null}
        <Route path="/facility/:slug" element={<FacilitiesDetail />} />
        <Route path="/facilities" element={<FacilitiesPageWrapper />} />
        <Route path="/drop-cv" element={<DropCv />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/appointment" element={<AppointmentContainer />} />
        <Route path="/gallery" element={<GalleryWrapper />} />
        <Route path="/gallery/:id" element={<GalleryDetail />} />
        <Route path="/hospital-clinic/:slug" element={<HospitalClinic />} />
        <Route path="/not-found" element={<PageNotFound />} />

        {patientLogin ? (
          <Route path="patient/*" element={<LoginDashboard />} />
        ) : (
          <Route path="patient/*" element={<Navigate to="/" />} />
        )}
      </Routes>
    </>
  );
}

export default Theme2Routes;
